import { MAX_PRACTICE_SLOTS } from '../domain/raidPracticeSlots.js';
import { useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { MarqueeText } from '../../../shared/components/MarqueeText.jsx';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

const CARD_STEP_PX = 224;
const DRAG_THRESHOLD_PX = 34;
const SETTLE_MS = 260;

function circularIndex(index, length) {
  return ((index % length) + length) % length;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function cardVisual(distance) {
  const absolute = Math.abs(distance);
  return {
    opacity: clamp(1 - absolute * 0.31, 0.2, 1),
    scale: clamp(1 - absolute * 0.16, 0.66, 1),
    brightness: clamp(1 - absolute * 0.23, 0.38, 1),
    zIndex: Math.max(1, 10 - Math.round(absolute * 2)),
  };
}

export function DesktopPracticeBossCarousel({ bosses = [], selectedBossNo, onSelect, compact = false }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const pointerRef = useRef({ id: null, startX: 0, moved: false });
  const suppressClickRef = useRef(false);
  const settleTimerRef = useRef(null);

  const selectedIndex = Math.max(0, bosses.findIndex((boss) => boss.bossNo === selectedBossNo));
  const visualIndex = selectedIndex + bosses.length;
  const looped = useMemo(() => {
    if (!Array.isArray(bosses) || bosses.length === 0) return [];
    return [...bosses, ...bosses, ...bosses];
  }, [bosses]);

  if (!looped.length) return null;

  if (compact) {
    return (
      <section className="desktop-practice-boss-compact" aria-label="간편보기 보스 선택">
        {bosses.map((boss) => {
          const active = Number(boss.bossNo) === Number(selectedBossNo);
          const practiceCount = Number(boss.practiceCount || 0);
          const actualCount = Number(boss.actualCount || 0);
          const displayName = bossDisplayName(boss);
          return (
            <button
              type="button"
              key={boss.bossNo}
              className="desktop-practice-boss-compact-item"
              data-active={active ? 'true' : 'false'}
              data-element={boss.element || ''}
              aria-pressed={active}
              aria-label={`${boss.bossNo}번 ${displayName}, 슬롯 ${practiceCount}/${MAX_PRACTICE_SLOTS}, 실전 ${actualCount}타`}
              onClick={() => onSelect(boss.bossNo)}
            >
              <span
                className="desktop-practice-boss-compact-image"
                style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }}
                aria-hidden="true"
              />
              <span className="desktop-practice-boss-compact-meta">
                <small>{boss.element || '속성'}</small>
                <strong title={displayName}>{displayName}</strong>
                <em>슬롯 {practiceCount}/{MAX_PRACTICE_SLOTS} · 실전 {actualCount}타</em>
              </span>
            </button>
          );
        })}
      </section>
    );
  }

  const selectAfterSlide = (steps) => {
    if (!steps || settling) return;
    const boundedSteps = clamp(steps, -(bosses.length - 1), bosses.length - 1);
    const next = bosses[circularIndex(selectedIndex + boundedSteps, bosses.length)];
    if (!next) return;

    setSettling(true);
    setDragX(-boundedSteps * CARD_STEP_PX);
    window.clearTimeout(settleTimerRef.current);
    settleTimerRef.current = window.setTimeout(() => {
      onSelect(next.bossNo);
      setDragX(0);
      setSettling(false);
    }, SETTLE_MS);
  };

  const finishDrag = (event, cancelled = false) => {
    if (pointerRef.current.id !== event.pointerId) return;
    const moved = pointerRef.current.moved;
    const distance = dragX;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    pointerRef.current = { id: null, startX: 0, moved: false };
    setDragging(false);

    if (moved) {
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }

    if (cancelled || Math.abs(distance) < DRAG_THRESHOLD_PX) {
      setSettling(true);
      setDragX(0);
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = window.setTimeout(() => setSettling(false), SETTLE_MS);
      return;
    }

    const steps = clamp(Math.round(-distance / CARD_STEP_PX), -(bosses.length - 1), bosses.length - 1)
      || (distance < 0 ? 1 : -1);
    selectAfterSlide(steps);
  };

  const progress = dragX / CARD_STEP_PX;
  const trackX = -(visualIndex * CARD_STEP_PX + 88) + dragX;

  return (
    <section className="desktop-practice-boss-carousel" aria-label="모의전 보스 선택">
      <div
        className={`desktop-practice-boss-carousel-viewport ${dragging ? 'dragging' : ''} ${settling ? 'settling' : ''}`}
        onPointerDown={(event) => {
          if (settling || (event.pointerType === 'mouse' && event.button !== 0)) return;
          pointerRef.current = { id: event.pointerId, startX: event.clientX, moved: false };
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
        }}
        onPointerMove={(event) => {
          if (pointerRef.current.id !== event.pointerId) return;
          const limit = CARD_STEP_PX * Math.max(2, bosses.length - 1);
          const distance = clamp(event.clientX - pointerRef.current.startX, -limit, limit);
          if (Math.abs(distance) > 5) pointerRef.current.moved = true;
          setDragX(distance);
        }}
        onPointerUp={(event) => finishDrag(event)}
        onPointerCancel={(event) => finishDrag(event, true)}
        onDragStart={(event) => event.preventDefault()}
      >
        <div className="desktop-practice-boss-carousel-track" style={{ transform: `translate3d(${trackX}px, -50%, 0)` }}>
          {looped.map((boss, itemIndex) => {
            const distance = itemIndex - visualIndex + progress;
            const visual = cardVisual(distance);
            const isActive = Math.abs(distance) < 0.5;
            return (
              <button
                type="button"
                key={`${boss.bossNo}-${itemIndex}`}
                className="desktop-practice-boss-carousel-card"
                data-active={isActive ? 'true' : 'false'}
                style={{
                  opacity: visual.opacity,
                  filter: `brightness(${visual.brightness})`,
                  transform: `scale(${visual.scale})`,
                  zIndex: visual.zIndex,
                }}
                onClick={() => {
                  if (suppressClickRef.current || dragging || settling) return;
                  const relativeSteps = itemIndex - visualIndex;
                  if (relativeSteps === 0) onSelect(boss.bossNo);
                  else selectAfterSlide(relativeSteps);
                }}
                aria-pressed={isActive}
              >
                <div className="desktop-practice-boss-carousel-image" style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }} />
                <div className="desktop-practice-boss-carousel-meta">
                  <div className="desktop-practice-boss-carousel-name">
                    <ElementIcon element={boss.element} />
                    <MarqueeText text={bossDisplayName(boss)} />
                  </div>
                  <small>슬롯 {boss.practiceCount || 0}</small>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
